import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import API_URL from '../utils/api';

const LeaveCalendar = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();

    useEffect(() => {
        fetchCalendar();
    }, [month, year]);

    const fetchCalendar = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/leaves/calendar?month=${month}&year=${year}`, { withCredentials: true });
            setEvents(data.events || []);
        } catch (error) {
            logger.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const prevMonth = () => setCurrentDate(new Date(year, month - 2, 1));
    const nextMonth = () => setCurrentDate(new Date(year, month, 1));

    const getDaysInMonth = (y, m) => new Date(y, m, 0).getDate();
    const getFirstDayOfMonth = (y, m) => new Date(y, m - 1, 1).getDay();

    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const getEventsForDay = (day) => {
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return events.filter(e => {
            const start = e.start_date.split('T')[0];
            const end = e.end_date.split('T')[0];
            return dateStr >= start && dateStr <= end;
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'bg-green-500';
            case 'rejected': return 'bg-red-500';
            case 'pending': return 'bg-amber-500';
            default: return 'bg-slate-400';
        }
    };

    const getLeaveTypeColor = (type) => {
        const colors = { annual: 'bg-blue-100 text-blue-800', sick: 'bg-red-100 text-red-800', casual: 'bg-purple-100 text-purple-800', maternity: 'bg-pink-100 text-pink-800', paternity: 'bg-teal-100 text-teal-800' };
        return colors[type] || 'bg-slate-100 text-slate-800';
    };

    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const today = new Date();
    const isToday = (day) => day === today.getDate() && month === today.getMonth() + 1 && year === today.getFullYear();

    return (
        <Card className="rounded-none border-slate-200" data-testid="leave-calendar">
            <CardHeader className="border-b border-slate-200">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {monthNames[month - 1]} {year}
                    </CardTitle>
                    <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="rounded-none" onClick={prevMonth} data-testid="cal-prev-month">
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="rounded-none" onClick={() => setCurrentDate(new Date())} data-testid="cal-today">
                            Today
                        </Button>
                        <Button variant="ghost" size="sm" className="rounded-none" onClick={nextMonth} data-testid="cal-next-month">
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-0">
                {loading ? (
                    <div className="py-12 text-center text-slate-500">Loading calendar...</div>
                ) : (
                    <>
                        <div className="grid grid-cols-7 border-b border-slate-200">
                            {dayNames.map(d => (
                                <div key={d} className="p-2 text-center text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-50">{d}</div>
                            ))}
                        </div>
                        <div className="grid grid-cols-7">
                            {cells.map((day, i) => {
                                const dayEvents = day ? getEventsForDay(day) : [];
                                return (
                                    <div key={i} className={`min-h-[80px] border-b border-r border-slate-100 p-1 ${!day ? 'bg-slate-50/50' : ''} ${isToday(day) ? 'bg-blue-50' : ''}`}>
                                        {day && (
                                            <>
                                                <div className={`text-xs font-medium mb-1 ${isToday(day) ? 'text-brand font-bold' : 'text-slate-600'}`}>{day}</div>
                                                <div className="space-y-0.5">
                                                    {dayEvents.slice(0, 3).map((e, j) => (
                                                        <div key={j} className={`text-[10px] px-1 py-0.5 truncate rounded-sm ${getLeaveTypeColor(e.leave_type)}`} title={`${e.employee_name} - ${e.leave_type} (${e.status})`}>
                                                            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${getStatusColor(e.status)}`} />
                                                            {e.employee_name}
                                                        </div>
                                                    ))}
                                                    {dayEvents.length > 3 && <div className="text-[10px] text-slate-500 px-1">+{dayEvents.length - 3} more</div>}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Legend */}
                        <div className="p-3 border-t border-slate-200 flex flex-wrap gap-3 text-xs">
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />Pending</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />Approved</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />Rejected</span>
                            <span className="mx-2 text-slate-300">|</span>
                            <Badge className="rounded-none bg-blue-100 text-blue-800 text-[10px]">Annual</Badge>
                            <Badge className="rounded-none bg-red-100 text-red-800 text-[10px]">Sick</Badge>
                            <Badge className="rounded-none bg-purple-100 text-purple-800 text-[10px]">Casual</Badge>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default LeaveCalendar;
